// src/cockpit4/components/ContentMatrixManager.tsx
// ============================================
// 内容矩阵管理（媒体工作室 P2-功能8）- v2.0 详细改进计划
// 账号矩阵管理 / 内容差异化策略 / 跨平台联动 / 矩阵数据看板 / 账号健康度
// 数据来源：localStorage（xeaos_content_matrix）
// ============================================

import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { LayoutGrid, AtSign, TrendingUp, RefreshCw, Layers } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

interface MatrixAccount {
  id: string;
  name: string;
  platform: "公众号" | "小红书" | "抖音" | "B站" | "知乎";
  role: "主号" | "子号";
  positioning: string;
  followers: number;
  weekPosts: number;
  avgViews: number;
  engagement: number;
  health: number;
}

const INITIAL_ACCOUNTS: MatrixAccount[] = [
  { id: "a1", name: "小舍AI-官方主号", platform: "公众号", role: "主号", positioning: "品牌背书 / 深度内容 / 产品发布", followers: 42800, weekPosts: 4, avgViews: 8200, engagement: 3.2, health: 92 },
  { id: "a2", name: "小舍AI-增长号", platform: "小红书", role: "子号", positioning: "干货种草 / 案例拆解 / 新人向", followers: 15600, weekPosts: 7, avgViews: 12500, engagement: 5.8, health: 88 },
  { id: "a3", name: "小舍AI-短视频号", platform: "抖音", role: "子号", positioning: "热点解读 / 快节奏科普", followers: 89000, weekPosts: 5, avgViews: 31000, engagement: 4.1, health: 85 },
  { id: "a4", name: "小舍AI-深度号", platform: "B站", role: "子号", positioning: "长视频教程 / 行业深度解析", followers: 22000, weekPosts: 2, avgViews: 9800, engagement: 6.5, health: 90 },
  { id: "a5", name: "小舍AI-专业号", platform: "知乎", role: "子号", positioning: "专业问答 / 行业观点输出", followers: 31000, weekPosts: 6, avgViews: 5400, engagement: 2.9, health: 78 },
];

const KEY = "xeaos_content_matrix";

function load(): MatrixAccount[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return JSON.parse(raw) as MatrixAccount[];
  } catch { /* ignore */ }
  return INITIAL_ACCOUNTS;
}

export default function ContentMatrixManager() {
  const [accounts, setAccounts] = useState<MatrixAccount[]>(load);
  const [selected, setSelected] = useState<string>("a2");

  const totalFollowers = useMemo(() => accounts.reduce((a, c) => a + c.followers, 0), [accounts]);
  const avgHealth = useMemo(() => accounts.reduce((a, c) => a + c.health, 0) / accounts.length, [accounts]);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <LayoutGrid size={14} className="text-purple-400" />
        <h3 className="text-[12px] font-medium text-white">内容矩阵管理</h3>
        <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">P2-功能8 · Content Matrix</span>
        <button
          onClick={() => setAccounts((prev) => {
            const next = prev.map((a) => ({ ...a, followers: a.followers + Math.round(a.followers * 0.01), avgViews: a.avgViews + Math.round(a.avgViews * 0.02) }));
            try { localStorage.setItem(KEY, JSON.stringify(next)); } catch { /* ignore */ }
            return next;
          })}
          className="flex items-center gap-1 ml-auto px-2 py-1 rounded-md border border-white/[0.06] bg-white/[0.02] text-[10px] text-gray-400 hover:text-white"
        >
          <RefreshCw size={10} /> 模拟刷新
        </button>
      </div>

      <div className="grid grid-cols-4 gap-2">
        <MetricCard label="矩阵账号" value={`${accounts.length} 个`} color="#94A3B8" />
        <MetricCard label="总粉丝" value={totalFollowers.toLocaleString()} color="#38BDF8" />
        <MetricCard label="平均健康度" value={`${avgHealth.toFixed(0)}/100`} color="#34D399" />
        <MetricCard label="本周发布" value={`${accounts.reduce((a, c) => a + c.weekPosts, 0)} 篇`} color="#A78BFA" />
      </div>

      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-7 rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
          <div className="flex items-center gap-2 mb-2">
            <AtSign size={11} className="text-purple-400" />
            <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">账号矩阵 · 点击查看健康度</span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {accounts.map((a) => {
              const isSel = a.id === selected;
              return (
                <button
                  key={a.id}
                  onClick={() => setSelected(a.id)}
                  className={`rounded-lg border p-2.5 text-left transition-colors ${isSel ? "border-purple-500/40 bg-purple-500/10" : "border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04]"}`}
                >
                  <div className="flex items-center gap-1.5">
                    <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded ${a.role === "主号" ? "bg-amber-500/15 text-amber-400" : "bg-sky-500/15 text-sky-400"}`}>{a.role}</span>
                    <span className="text-[10px] font-mono text-gray-500">{a.platform}</span>
                  </div>
                  <div className="text-[10px] font-medium text-white mt-1">{a.name}</div>
                  <div className="text-[9px] font-mono text-gray-600 mt-0.5">{a.positioning}</div>
                  <div className="flex items-center gap-3 mt-1.5 text-[9px] font-mono text-gray-500">
                    <span>{a.followers.toLocaleString()} 粉</span>
                    <span>{a.weekPosts} 篇/周</span>
                    <span className="ml-auto">健康度</span>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                      <motion.div className="h-full rounded-full" initial={{ width: 0 }} animate={{ width: `${a.health}%` }} transition={{ duration: 0.5 }} style={{ background: a.health >= 85 ? "#34D399" : a.health >= 70 ? "#FBBF24" : "#F87171" }} />
                    </div>
                    <span className="text-[9px] font-mono" style={{ color: a.health >= 85 ? "#34D399" : a.health >= 70 ? "#FBBF24" : "#F87171" }}>{a.health}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>


        <div className="col-span-5 rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp size={11} className="text-emerald-400" />
            <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">矩阵整体数据 · 各账号周均阅读</span>
          </div>
          <div className="h-[170px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={accounts.map((a) => ({ name: a.platform, 阅读: a.avgViews }))} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fill: "#64748B", fontSize: 9, fontFamily: "monospace" }} />
                <YAxis tick={{ fill: "#64748B", fontSize: 9, fontFamily: "monospace" }} width={40} />
                <Tooltip contentStyle={{ background: "#0a0e1a", border: "1px solid #1e2a3f", borderRadius: 6, fontSize: 11, fontFamily: "monospace" }} labelStyle={{ color: "#E5E7EB" }} formatter={(v: any) => [Number(v).toLocaleString(), "周均阅读"]} />
                <Bar dataKey="阅读" fill="#A78BFA" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-2 space-y-1.5 text-[9px] font-mono">
            {[
              { color: "#34D399", text: "跨平台联动：小红书种草 → 公众号深度 → 抖音短视频 三号联动策略运行中" },
              { color: "#FBBF24", text: "知乎号健康度 78 偏低：建议提升发布频率至每周 4 篇" },
            ].map((s, i) => (
              <div key={i} className="flex items-start gap-1.5 rounded-md border border-white/[0.04] bg-white/[0.01] p-2">
                <span className="w-1.5 h-1.5 rounded-full mt-1 flex-shrink-0" style={{ background: s.color }} />
                <span className="text-gray-400 leading-relaxed">{s.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function MetricCard({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2">
      <div className="text-[9px] font-mono text-gray-500 uppercase">{label}</div>
      <div className="text-sm font-semibold font-mono mt-0.5" style={{ color }}>{value}</div>
    </motion.div>
  );
}

